import { Container, Inject } from 'typescript-ioc';
import { action, observable } from 'mobx';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../../infrastructure/sub-windows';
import { CustomFieldsOptionsListDTO, CustomPageType, DefaultPageType, IMeetingPresenterGroupDTO, SessionPageLayoutOptions, UpcomingSessionsTypeOptions } from '../../../../../../api/custom-pages.dao';
import { IItemProps } from '../../../../../global/multi-selection-dropdown/multi-selection-dropdown';
import { CustomPagePresentation } from './custom-pages/custom-page.presentation';
import { CustomPageFormViewModel } from './custom-pages/custom-page.viewmodel';
import { DefaultPagePresentation } from './default-pages/default-page.presentation';
import { DefaultPageViewModel } from './default-pages/default-page.viewmodel';

type HandlersProps = Record<string, { handler: () => Promise<void>; shouldClose?: boolean }>;
type CustomPageSubWindowProps = {
    pageVm: CustomPageFormViewModel;
    title: string;
    types: CustomPageType[];
    handlers: HandlersProps;
    organizationTypes?: string[];
    customFieldsOptionsList?: CustomFieldsOptionsListDTO[];
    meetingPresenterGroups?: IMeetingPresenterGroupDTO[];
};
type PageKey = number | string | DefaultPageType;

type DefaultPageSubWindowProps = {
    pageVm: DefaultPageViewModel;
    title: string;
    handlers: HandlersProps;
    sessionsLayoutOptions?: SessionPageLayoutOptions[];
    upcomingSessionsTypeOptions?: UpcomingSessionsTypeOptions[];
    fetchOrganizationTypes?: (query: string) => Promise<IItemProps[]>;
    customFieldsOptionsList?: CustomFieldsOptionsListDTO[];
};

export class PassportPageSubWindowService {
    public static init(): PassportPageSubWindowService {
        if (!this.instance) this.instance = new PassportPageSubWindowService();
        return this.instance;
    }
    private static instance: PassportPageSubWindowService;

    @action.bound
    public openCreateCustomPage(props: CustomPageSubWindowProps): void {
        this.openCustomPageSubWindow({ ...props, isEditPage: false });
    }

    @action
    public openEditCustomPage(props: CustomPageSubWindowProps): void {
        if (this.hasSubWindowOpened(props.pageVm.getId())) {
            this.subWindows.get(props.pageVm.getId()).expand();
        } else {
           this.openCustomPageSubWindow({ ...props, isEditPage: true });
        }
    }

    @action.bound
    public openEditDefaultPage(props: DefaultPageSubWindowProps): void {
        if (this.hasSubWindowOpened(props.pageVm.getType())) {
            this.maximizeSubWindowByKey(props.pageVm.getType());
        } else {
            this.openDefaultPageSubWindow(props);
        }
    }

    public hasSubWindowOpened(key: PageKey): boolean {
        return this.subWindows.has(key) && this.subWindows.get(key).getStatus() !== SubWindowStatus.Closed;
    }

    public maximizeSubWindowByKey(key: PageKey): void {
        if (this.subWindows.has(key)) this.subWindows.get(key).maximize();
    }

    private openCustomPageSubWindow(props: CustomPageSubWindowProps & {isEditPage: boolean}): void {
        const { title, handlers, isEditPage, ...rest } = props;

        const subWindow = this.subWindowService.showSubWindow({
            title: title,
            isExpandedByDefault: true,
            componentToHost: CustomPagePresentation,
            componentToHostProps: {
                isEditPage: isEditPage,
                onCancel: this.setAsClosed.bind(this, props.pageVm.getId()),
                ...rest,
                ...Object
                    .entries(handlers )
                    .reduce((_handlers, [name, { handler, shouldClose }]) => {
                        _handlers[name] = async () => {
                            await handler();
                            if (shouldClose) subWindow.close();
                        };
                        return _handlers;
                    }, {})
            }
        });

        if (subWindow) this.setAsOpened(props.pageVm.getId(), subWindow);
    }

    private openDefaultPageSubWindow(props: DefaultPageSubWindowProps): void {
        const { title, handlers, ...rest } = props;

        const subWindow = this.subWindowService.showSubWindow({
            title: title,
            componentToHost: DefaultPagePresentation,
            componentToHostProps: {
                onCancel: this.setAsClosed.bind(this, props.pageVm.getType()),
                ...rest,
                ...Object
                    .entries(handlers )
                    .reduce((_handlers, [name, { handler, shouldClose }]) => {
                        _handlers[name] = async () => {
                            await handler();
                            if (shouldClose) subWindow.close();
                        };
                        return _handlers;
                    }, {})
            }
        });

        if (subWindow) this.setAsOpened(props.pageVm.getType(), subWindow);
    }

    private setAsOpened(key: PageKey, subWindow: ISubWindowFacade): void {
        this.subWindows.set(key, subWindow);
    }
    private setAsClosed(key: PageKey): void {
        if (this.subWindows.has(key)) this.subWindows.delete(key);
    }

    private constructor(@Inject private readonly subWindowService: ISubWindowsService = Container.get(ISubWindowsService)) {}

    @observable
    public readonly subWindows: Map<PageKey, ISubWindowFacade> = new Map();
}
